import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Features.css';
import DifferentiatorSection from './DifferentiatorSection';
import WindowsFeature from './WindowsFeature';
import {
  ConversationalExplorationFeature,
  AISuggestionsFeature,
  ApiStepsFeature,
  DiscoveryProFeature,
  AITestHealingFeature,
  AIBugCreationFeature,
  AIBugSolvingFeature,
  GitHubCopilotFeature,
  KnowledgeBaseFeature,
  EnterpriseIntegrationsFeature,
  ReportsInsightsFeature,
  NoVendorLockInFeature,
  VisualModelFeature,
  MFAFeature,
  PRProcessingFeature
} from './features/index';

const Features = () => {
  const featuresRef = useRef(null);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;

    const showAllElements = () => {
      const elements = featuresRef.current?.querySelectorAll('.features-item');
      elements?.forEach((el) => {
        el.classList.add('visible');
      });
    };

    if (isMobile) {
      showAllElements();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -20px 0px'
      }
    );

    const elements = featuresRef.current?.querySelectorAll('.features-item');
    if (elements && elements.length > 0) {
      elements.forEach((el) => observer.observe(el));
    } else {
      setTimeout(showAllElements, 100);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    { ...ConversationalExplorationFeature, id: 'conversational-exploration' },
    { ...WindowsFeature, id: 'windows-desktop' },
    { ...AISuggestionsFeature, id: 'ai-suggestions' },
    { ...ApiStepsFeature, id: 'api-steps' },
    { ...DiscoveryProFeature, id: 'discovery' },
    { ...AITestHealingFeature, id: 'ai-test-healing' },
    { ...AIBugCreationFeature, id: 'ai-bug-detection' },
    { ...AIBugSolvingFeature, id: 'ai-bug-solving' },
    { ...GitHubCopilotFeature, id: 'github-copilot' },
    { ...PRProcessingFeature, id: 'pr-processing' },
    { ...KnowledgeBaseFeature, id: 'knowledge-base' },
    { ...MFAFeature, id: 'mfa-totp' },
    { ...EnterpriseIntegrationsFeature, id: 'enterprise-integrations' },
    { ...ReportsInsightsFeature, id: 'reports-insights' },
    { ...VisualModelFeature, id: 'visual-model' },
    { ...NoVendorLockInFeature, id: 'no-vendor-lock-in' }
  ];

  const byId = Object.fromEntries(features.map((f) => [f.id, f]));

  // Three flagship capabilities get the full deep-dive treatment with their
  // live visuals. Everything else is grouped into a compact capability grid.
  const FLAGSHIP_IDS = ['conversational-exploration', 'windows-desktop', 'ai-bug-solving'];
  const flagships = FLAGSHIP_IDS.map((id) => byId[id]).filter(Boolean);

  const GROUPS = [
    {
      title: 'Create tests by talking',
      blurb: 'Describe what to test in plain language — Qate explores your app and writes the tests.',
      ids: ['ai-suggestions', 'api-steps', 'discovery', 'knowledge-base'],
    },
    {
      title: 'Find and fix what breaks',
      blurb: 'When a test fails, Qate explains why — and proposes the fix.',
      ids: ['ai-test-healing', 'ai-bug-detection', 'github-copilot', 'pr-processing'],
    },
    {
      title: 'Run anywhere, scale to the enterprise',
      blurb: 'Your tests on your infrastructure, with the controls larger teams need.',
      ids: ['visual-model', 'enterprise-integrations', 'reports-insights', 'mfa-totp', 'no-vendor-lock-in'],
    },
  ];

  return (
    <section className="features" id="features" ref={featuresRef}>
      <div className="features-container">
        {/* Differentiator Section */}
        <DifferentiatorSection />

        <div className="section-header">
          <span className="section-eyebrow">Capabilities</span>
          <h2>Powerful features, simple conversations</h2>
          <p>Qate understands what you want, builds tests while you talk, and lets you run them anywhere.</p>
        </div>

        {/* Flagship capabilities — full deep-dives with live visuals */}
        <div className="features-flagships">
          {flagships.map((feature, index) => (
            <div
              key={feature.id}
              id={feature.id}
              className={`features-item ${index % 2 === 0 ? 'left' : 'right'} ${feature.id === 'windows-desktop' ? 'windows-desktop-feature' : ''}`}
            >
              {feature.id === 'windows-desktop' && <div className="card-background"></div>}
              <div className="feature-content">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                {feature.highlight && (
                  Array.isArray(feature.highlight) ? (
                    <div className="feature-highlights">
                      {feature.highlight.map((h, i) => (
                        <span key={i} className="feature-highlight">{h}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="feature-highlight">{feature.highlight}</span>
                  )
                )}
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                {feature.docsLink && (
                  <Link to={feature.docsLink.url} className="feature-docs-link">
                    {feature.docsLink.label}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                )}
                {feature.features && (
                  <ul className="feature-list">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="feature-list-item">
                        <div className="feature-list-icon">{item.icon}</div>
                        <div className="feature-list-text" dangerouslySetInnerHTML={{ __html: item.text }}></div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="feature-visual">
                {feature.animation}
              </div>
            </div>
          ))}
        </div>

        {/* Everything else — grouped capability grid */}
        <div className="feature-groups">
          {GROUPS.map((group) => (
            <div className="feature-group" key={group.title}>
              <div className="feature-group-head">
                <h3 className="feature-group-title">{group.title}</h3>
                <p className="feature-group-blurb">{group.blurb}</p>
              </div>
              <div className="feature-grid">
                {group.ids.map((id) => byId[id]).filter(Boolean).map((feature) => (
                  <div className="feature-card" id={feature.id} key={feature.id}>
                    <div className="feature-card-icon">{feature.icon}</div>
                    <h4 className="feature-card-title">{feature.title}</h4>
                    <p className="feature-card-desc">{feature.description}</p>
                    {feature.docsLink && (
                      <Link to={feature.docsLink.url} className="feature-card-link">
                        {feature.docsLink.label}
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
